import type {
  LibraryCourseNode,
  LibraryLessonNode,
  LibraryModuleNode,
  LibraryResponse,
} from '@/api/lcLearning'
import type {
  Course,
  CurriculumAccent,
  Lesson,
  LibraryData,
  LibraryModule,
  SubjectColor,
} from '../ui-model/types'

const COURSE_COLORS: SubjectColor[] = ['orange', 'blue', 'mint', 'purple', 'pink']
const COURSE_TITLE_SEPARATOR = /[\s_-]+/gu
const CORE_COURSE_TITLE = /^(?:CORE|코어)([A-E])(\d*)$/iu
const ADVANCED_COURSE_TITLE = /^(?:ADVANCED|어드밴스[드트])\d*$/iu
const ELITE_COURSE_TITLE = /^(?:ELITE|엘리트)\d*$/iu

export type CourseTitleType = 'core' | 'advanced' | 'elite' | 'other'

export interface CoreCourseIdentity {
  code: 'A' | 'B' | 'C' | 'D' | 'E'
  memberNumber: number | null
  journeyGroupKey: 'core-a' | 'core-b' | null
}

export function parseCoreCourseIdentity(title: string): CoreCourseIdentity | null {
  const compactTitle = title.trim().replace(COURSE_TITLE_SEPARATOR, '')
  const match = compactTitle.match(CORE_COURSE_TITLE)
  if (!match) return null

  const code = match[1].toUpperCase() as CoreCourseIdentity['code']
  const memberNumber = match[2] ? Number(match[2]) : null
  return {
    code,
    memberNumber,
    journeyGroupKey: code === 'A' ? 'core-a' : code === 'B' ? 'core-b' : null,
  }
}

export function classifyCourseTitle(title: string): CourseTitleType {
  const compactTitle = title.trim().replace(COURSE_TITLE_SEPARATOR, '')
  if (CORE_COURSE_TITLE.test(compactTitle)) return 'core'
  if (ADVANCED_COURSE_TITLE.test(compactTitle)) return 'advanced'
  if (ELITE_COURSE_TITLE.test(compactTitle)) return 'elite'
  return 'other'
}

function pickCourseColor(index: number): SubjectColor {
  return COURSE_COLORS[index % COURSE_COLORS.length]
}

function getBadgeLabel(title: string): string {
  const normalizedTitle = title.trim()
  const coreIdentity = parseCoreCourseIdentity(normalizedTitle)
  if (coreIdentity) {
    return `${coreIdentity.code}${coreIdentity.memberNumber ?? ''}`
  }
  const courseTitleType = classifyCourseTitle(normalizedTitle)
  if (courseTitleType === 'advanced') return 'AD'
  if (courseTitleType === 'elite') return 'EL'

  const trailingCode = normalizedTitle.match(/(?:[-_\s])([A-Za-z0-9])$/)?.[1]
  if (trailingCode) return trailingCode.toUpperCase()

  const firstCharacter = Array.from(normalizedTitle)[0]
  return firstCharacter?.toLocaleUpperCase() ?? '•'
}

export function getCourseAccent(title: string): CurriculumAccent {
  const coreIdentity = parseCoreCourseIdentity(title)
  if (coreIdentity) return `core-${coreIdentity.code.toLowerCase()}` as CurriculumAccent

  const titleType = classifyCourseTitle(title)
  if (titleType === 'advanced' || titleType === 'elite') return titleType
  return 'key'
}

function toLesson(node: LibraryLessonNode, order: number, color: SubjectColor): Lesson {
  const done = node.status === 'completed'
  const inProgress = node.status === 'in_progress'
  return {
    id: node.contentId,
    order,
    title: node.title,
    status: done ? 'done' : inProgress ? 'current' : 'locked',
    progressPercent: done ? 100 : undefined,
    thumbColor: color,
    thumbnailUrl: node.thumbnailUrl,
    completedAt: node.completedAt,
  }
}

function toModule(node: LibraryModuleNode, order: number, color: SubjectColor): LibraryModule {
  const lessons = node.lessons.map((lesson, index) => toLesson(lesson, index + 1, color))
  const completedLessons = lessons.filter((l) => l.status === 'done').length
  return {
    id: node.contentId,
    order,
    title: node.title,
    totalLessons: lessons.length,
    completedLessons,
    lessons,
  }
}

function toCourse(node: LibraryCourseNode, idx: number): Course {
  const color = pickCourseColor(idx)
  const modules = node.modules.map((module, index) => toModule(module, index + 1, color))
  const totalLessons = modules.reduce((acc, m) => acc + m.totalLessons, 0)
  const completedLessons = modules.reduce((acc, m) => acc + m.completedLessons, 0)
  return {
    id: node.contentId,
    title: node.title,
    badgeLabel: getBadgeLabel(node.title),
    accent: getCourseAccent(node.title),
    totalModules: modules.length,
    totalLessons,
    completedLessons,
    modules,
  }
}

/** 현재 진행 코스 → 첫 미완료 코스 → 마지막 학습 코스 순으로 기본 탭을 고른다. */
export function findCurrentCourseId(courses: Course[]): number | null {
  const inProgressCourse = courses.find((course) =>
    course.modules.some((module) =>
      module.lessons.some((lesson) => lesson.status === 'current'),
    ),
  )
  if (inProgressCourse) return inProgressCourse.id

  const incompleteCourse = courses.find(
    (course) => course.totalLessons > 0 && course.completedLessons < course.totalLessons,
  )
  if (incompleteCourse) return incompleteCourse.id

  const nonEmptyCourses = courses.filter((course) => course.totalLessons > 0)
  return nonEmptyCourses.at(-1)?.id ?? courses[0]?.id ?? null
}

export function toLibraryData(resp: LibraryResponse): LibraryData {
  return {
    enrollmentId: resp.enrollmentId,
    productName: resp.productName,
    curriculumTitle: resp.curriculum.title,
    courses: resp.curriculum.courses.map((c, i) => toCourse(c, i)),
  }
}
